import { useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";
import Card from "@mui/material/Card";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

const DEBUG_CHARTS = false;

function EarningsChart({ data }) {
  const months = useMemo(() => ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"], []);
  const stats = data?.earnings || data || {};
  const byYear = stats.byYear || {};

  const currentYear = new Date().getFullYear();
  const years = useMemo(() => Object.keys(byYear).map(Number).filter(Number.isFinite).sort((a,b)=>a-b), [byYear]);
  const minYear = years.length ? years[0] : currentYear;
  const maxYear = years.length ? years[years.length - 1] : currentYear;

  const [year, setYear] = useState(() => (years.includes(currentYear) ? currentYear : maxYear));
  const bucket = byYear?.[year] || byYear?.[String(year)] || null;

  const normalize12 = (arr) => Array.from({ length: 12 }, (_, i) => Number(arr?.[i] ?? 0) || 0);

  const tier1 = normalize12(bucket?.tier1Earnings);
  const tier2 = normalize12(bucket?.tier2Earnings);
  const tier3 = normalize12(bucket?.tier3Earnings);

  if (DEBUG_CHARTS) {
    console.log("=== CHART DEBUG: EarningsChart ===");
    console.log("data prop:", data ? Object.keys(data) : "NO DATA");
    console.log("stats keys:", stats ? Object.keys(stats) : "NO STATS");
    console.log("byYear keys:", Object.keys(byYear || {}));
    console.log("selected year:", year, "bucket?", !!bucket);
    if (bucket) console.log("bucket sample:", bucket);
    console.log("tier1 sum:", tier1.reduce((a,b)=>a+b,0), "tier2 sum:", tier2.reduce((a,b)=>a+b,0), "tier3 sum:", tier3.reduce((a,b)=>a+b,0));
  }

  const maxEarnings = Math.max(...tier1, ...tier2, ...tier3, 0);
  const yAxisMax = Math.max(10, Math.ceil(maxEarnings * 1.2));

  const hasAnyData = tier1.some((n) => n > 0) || tier2.some((n) => n > 0) || tier3.some((n) => n > 0);



  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
          <MDTypography variant="h6">Monthly Earnings by Tier</MDTypography>

          <MDBox display="flex" alignItems="center" gap={1}>
            <IconButton size="small" onClick={() => setYear((y) => Math.max(minYear, y - 1))} disabled={year <= minYear}>
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <MDTypography variant="button" fontWeight="medium">{year}</MDTypography>
            <IconButton size="small" onClick={() => setYear((y) => Math.min(maxYear, y + 1))} disabled={year >= maxYear}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </MDBox>
        </MDBox>

        <Bar
          data={{
            labels: months,
            datasets: [
              { label: "Tier 1 (20%)", data: tier1, stack: "s", backgroundColor: "rgba(75, 192, 192, 0.8)" },
              { label: "Tier 2 (30%)", data: tier2, stack: "s", backgroundColor: "rgba(153, 102, 255, 0.8)" },
              { label: "Tier 3 (40%)", data: tier3, stack: "s", backgroundColor: "rgba(255, 159, 64, 0.8)" },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            scales: {
              x: { stacked: true },
              y: { stacked: true, beginAtZero: true, max: yAxisMax, ticks: { callback: (v) => `$${Number(v).toFixed(2)}` } },
            },
            plugins: { legend: { position: "bottom" } },
          }}
        />
      </MDBox>
    </Card>
  );
}

export default EarningsChart;